import { Users } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { PLATFORM_TAG_COPY, type FeatureFlagDefinition } from '@/types/feature-flag';

interface FlagRowIdentityProps {
  definition: FeatureFlagDefinition;
}

/**
 * The name, key, badges and description at the head of a flag row — shared so
 * the global and tenant pages cannot drift apart on what a flag looks like.
 *
 * The platform badge says which surface the flag affects. It is descriptive:
 * delivery is decided by the gates panel, never by this.
 */
export function FlagRowIdentity({ definition }: FlagRowIdentityProps) {
  const platform = PLATFORM_TAG_COPY[definition.platformTag];

  return (
    <div className="min-w-0 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold">{definition.label}</span>
        <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{definition.key}</code>
        <Badge variant="outline" className={cn(platform.className)}>
          {platform.label}
        </Badge>
        {definition.isBuiltIn && <Badge variant="outline">built-in</Badge>}
        <Badge variant={definition.enforced ? 'destructive' : 'secondary'}>
          {definition.enforced ? 'Blocks traffic' : 'Client-read only'}
        </Badge>
        {definition.ruleCount > 0 && (
          <Badge variant="outline">
            <Users className="mr-1 h-3 w-3" />
            {definition.ruleCount} targeted
          </Badge>
        )}
      </div>
      {definition.description ? (
        <p className="text-sm text-muted-foreground">{definition.description}</p>
      ) : (
        <p className="text-sm italic text-muted-foreground/60">No description yet</p>
      )}
    </div>
  );
}
